import React, { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import {
  MachineStatusNames,
  MachineStatusTypes as OptionType
} from "interfaces/machine-status-types";
import { Autocomplete } from "components/Autocomplete";
import { useLazyGetMachineStatusTypes } from "hooks/machine-status-types/useLazyGetMachineStatusTypes";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface MachineStatusFilterProps {
  selectedMachineStatuses: OptionType[];
  onChangeSelectedMachineStatuses: (values: OptionType[]) => void;
  showChips?: boolean;
}

const selectAllOption: OptionType = {
  id: -1,
  name: "Select All" as MachineStatusNames, // FFS
  order: 0
};

export const MachineStatusFilter = ({
  selectedMachineStatuses,
  onChangeSelectedMachineStatuses,
  showChips = true
}: MachineStatusFilterProps): JSX.Element => {
  const {
    loading: machineStatusTypeLoading,
    error: machineStatusTypeError,
    fetchMachineStatusTypes: getMachineStatusTypes
  } = useLazyGetMachineStatusTypes();
  const [options, setOptions] = useState<OptionType[] | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState("");

  const onFocus = useCallback(() => {
    /** If it is the first focus event, get options. */
    if (options === undefined) {
      getMachineStatusTypes().then(response => {
        if (response.results) {
          setOptions([selectAllOption, ...response.results]);
        }
      });
    }
    setSelectedState(selectedMachineStatuses);
  }, [options, getMachineStatusTypes, selectedMachineStatuses]);

  return (
    <StyledAutocomplete
      label="Machine Status"
      placeholder={showChips ? "Machine Status" : ""}
      loading={machineStatusTypeLoading}
      error={machineStatusTypeError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options ?? []}
      noOptionsText="No results found"
      getOptionLabel={(option: OptionType) => option.name}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.name === value.name
      }
      value={selectedMachineStatuses}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: OptionType,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(
          option => option.name !== ("Select All" as MachineStatusNames)
        );

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );

        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: OptionType[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(
          option => option.name !== ("Select All" as MachineStatusNames)
        );

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );

        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              option =>
                !filteredSelectedState.some(
                  selected => selected.id === option.id
                )
            );
            onChangeSelectedMachineStatuses(selected);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedMachineStatuses, ...filteredNonSelectAllOptions],
              "id"
            );

            // If not everything was selected, select all
            onChangeSelectedMachineStatuses(selected);
            setSelectedState(selected);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedMachineStatuses(values);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
